"use client";

import React, { useRef, useState } from "react";
import type { FormPayload } from "@/app/interface/components";
import InputMask from "@/app/services/InputMask";
import { sendToSyonet } from "@/app/services/syonetIntegration";
import { pushLeadForm } from "@/app/utils/gtm";
import {
	MOVE_BRASIL_FORM_ID,
	MOVE_BRASIL_MODELS,
	MOVE_BRASIL_PRIVACY_URL,
	MOVE_BRASIL_SYONET_EVENT_TYPE,
	MOVE_BRASIL_SYONET_PAGE,
	MOVE_BRASIL_UNITS,
} from "@/app/config/moveBrasil";
import styles from "@/styles/move-brasil.module.scss";

function digitsOnly(value: string) {
	return value.replace(/\D/g, "");
}

export const ContactSection: React.FC = () => {
	const formRef = useRef<HTMLFormElement>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState(false);

	const onSubmit = async (ev: React.FormEvent<HTMLFormElement>) => {
		ev.preventDefault();
		setError(null);

		const form = formRef.current;
		if (!form) return;

		const nome =
			(form.elements.namedItem("nome") as HTMLInputElement)?.value ?? "";
		const cpf =
			(form.elements.namedItem("cpf") as HTMLInputElement)?.value ?? "";
		const email =
			(form.elements.namedItem("email") as HTMLInputElement)?.value ?? "";
		const telefone =
			(form.elements.namedItem("telefone") as HTMLInputElement)?.value ?? "";
		const unidade =
			(form.elements.namedItem("unidade") as HTMLSelectElement)?.value ?? "";
		const modelo =
			(form.elements.namedItem("modelo") as HTMLSelectElement)?.value ?? "";

		if (!nome.trim()) {
			setError("Preencha o nome.");
			return;
		}
		if (!email.trim()) {
			setError("Preencha o e-mail.");
			return;
		}
		if (!telefone.trim()) {
			setError("Preencha o telefone.");
			return;
		}
		if (!cpf.trim() || digitsOnly(cpf).length !== 11) {
			setError("Informe um CPF válido com 11 dígitos.");
			return;
		}
		if (!unidade) {
			setError("Selecione a unidade.");
			return;
		}
		if (!modelo) {
			setError("Selecione o modelo.");
			return;
		}

		const selectedUnit = MOVE_BRASIL_UNITS.find(
			(unit) => String(unit.id) === unidade,
		);

		const payload: FormPayload = {
			name: nome.trim(),
			email: email.trim(),
			cellPhone: digitsOnly(telefone),
			registrationNumber: digitsOnly(cpf),
			consorcio: false,
			team: "Veiculos Novos",
			locationId: selectedUnit?.id ?? 1,
			dealer: selectedUnit?.nome,
			model: modelo,
			page: MOVE_BRASIL_SYONET_PAGE,
			comment: "[SITE - MOVE BRASIL] Solicitação de proposta",
		};

		setSubmitting(true);
		try {
			const ok = await sendToSyonet(
				{ ...payload, eventType: MOVE_BRASIL_SYONET_EVENT_TYPE } as FormPayload,
				"jeep",
				{
					skipToyotaLeadCenter: true,
					companyId: unidade,
					eventType: MOVE_BRASIL_SYONET_EVENT_TYPE,
				},
			);
			if (!ok) {
				throw new Error(
					"Não foi possível enviar sua solicitação. Tente novamente.",
				);
			}
			pushLeadForm();
			form.reset();
			setSuccess(true);
		} catch (e) {
			setError(
				e instanceof Error
					? e.message
					: "Não foi possível enviar sua solicitação. Tente novamente.",
			);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section id={MOVE_BRASIL_FORM_ID} className={styles.contact}>
			<div className={styles.container}>
				<div className={styles.contactGrid}>
					<div className={styles.contactIntro}>
						<h2 className={styles.contactTitle}>
							Não deixe essa oportunidade passar. Faça sua simulação.
						</h2>
						<p className={styles.contactText}>
							Confirme sua elegibilidade ao Programa Move Brasil e descubra
							quanto você pode economizar no Jeep Renegade Longitude T270
							MHEV 4x2. Converse com um especialista da Jeep.
						</p>					</div>

					<div className={styles.contactCard}>
						<header className={styles.contactCardHeader}>
							<h3 className={styles.contactCardTitle}>
								<span className={styles.contactCardTitleAccent}>
									Fale
								</span>{" "}
								com um consultor
							</h3>
							<p className={styles.contactCardSubtitle}>
								Preencha o formulário abaixo para receber o contato de
								um de nossos especialistas:
							</p>
						</header>
						{success ? (
							<p className={styles.contactSuccess}>
								Solicitação enviada com sucesso! Em breve entraremos em
								contato.
							</p>
						) : (
							<form
								ref={formRef}
								className={styles.contactForm}
								onSubmit={onSubmit}
							>
								<div className={styles.contactFields}>
									<p className={styles.contactFieldsLabel}>Meus dados</p>

									<label className={styles.contactField}>
										<input
											type="text"
											name="nome"
											className={styles.contactInput}
											placeholder="Nome*"
											required
										/>
									</label>

									<label className={styles.contactField}>
										<input
											type="email"
											name="email"
											className={styles.contactInput}
											placeholder="E-mail*"
											required
										/>
									</label>

									<label className={styles.contactField}>
										<InputMask
											type="tel"
											id="move-brasil-phone"
											name="telefone"
											placeholder="Telefone/Whatsapp*"
											className={styles.contactInput}
											aria-label="Telefone/Whatsapp"
											required
										/>
									</label>

									<label className={styles.contactField}>
										<InputMask
											type="cpf"
											id="move-brasil-cpf"
											name="cpf"
											placeholder="CPF*"
											className={styles.contactInput}
											aria-label="CPF"
											required
										/>
									</label>

									<label className={styles.contactField}>
										<select
											name="modelo"
											className={styles.contactSelect}
											defaultValue=""
											required
											aria-label="Selecione o modelo"
										>
											<option value="" disabled>
												Selecione o modelo
											</option>
											{MOVE_BRASIL_MODELS.map((model) => (
												<option key={model} value={model}>
													{model}
												</option>
											))}
										</select>
									</label>

									<label className={styles.contactField}>
										<select
											name="unidade"
											className={styles.contactSelect}
											defaultValue=""
											required
											aria-label="Selecione a unidade"
										>
											<option value="" disabled>
												Selecione a unidade
											</option>
											{MOVE_BRASIL_UNITS.map((unit) => (
												<option key={unit.id} value={unit.id}>
													{unit.nome}
												</option>
											))}
										</select>
									</label>
								</div>

								{error && (
									<p className={styles.contactError} role="alert">
										{error}
									</p>
								)}

								<p className={styles.contactPrivacy}>
									Ao informar meus dados, eu concordo com a{" "}
									<a
										href={MOVE_BRASIL_PRIVACY_URL}
										target="_blank"
										rel="noopener noreferrer"
									>
										política de privacidade.
									</a>
								</p>

								<div className={styles.contactCtaWrap}>
									<button
										type="submit"
										className={styles.contactCta}
										disabled={submitting}
									>
										{submitting ? "ENVIANDO..." : "SOLICITAR PROPOSTA"}
									</button>
								</div>
							</form>
						)}
					</div>
				</div>
			</div>
		</section>
	);
};
